import React, { useState, useEffect, useRef } from 'react';
import { Users, MessageSquare, TrendingUp, Calendar, Sparkles, Send, Copy, Check, ChevronDown, ChevronUp, Loader, X, Save } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { contactService, aiService } from '../services/api';
import ContactCard from '../componets/ContactCard';
import { useNavigate } from 'react-router-dom';
import { useToast, ToastContainer } from '../componets/Toast';
import UpcomingDates from '../componets/UpcomingDate';
import API_URL from '../config/API_URL';
import moment from 'moment';
import PaywallModal from '../subscription/PaywallModal';
const Dashboard = ({ onSelectContact }) => {
  const [contacts, setContacts] = useState([]);
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();

  // Quick message generation state
  const [quickMessageContact, setQuickMessageContact] = useState('');
  const [quickMessageType, setQuickMessageType] = useState('follow_up');
  const [quickMessageMedium, setQuickMessageMedium] = useState('email');
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // AI Connector chat state
  const [aiConnectorOpen, setAiConnectorOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isChatGenerating, setIsChatGenerating] = useState(false);
  const [connectionSuggestions, setConnectionSuggestions] = useState([]);
  const [addingConnection, setAddingConnection] = useState(false);
  const [scheduleEventModalOpen, setScheduleEventModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);

  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeatureInfo, setPaywallFeatureInfo] = useState(null);

  const chatEndRef = useRef(null);

  // Schedule event form state
  const [newEvent, setNewEvent] = useState({
    title: '',
    contact_id: '',
    event_type: 'coffee',
    start_date: '',
    start_time: '',
    duration: 30,
    notes: '',
    reminder: true,
  });

  const [stats, setStats] = useState({
    totalContacts: 0,
    recentInteractions: 0,
    activeConnections: 0,
  });

  const styles = {
    page: {
      padding: '2rem',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    subtitle: {
      color: '#475569',
      fontSize: '1rem',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    statCard: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '1.5rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem',
      transition: 'all 200ms',
    },
    iconBox: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    statInfo: {
      flex: 1,
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#64748b',
      marginBottom: '0.25rem',
      fontWeight: 500,
    },
    statValue: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
    },
    section: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '1rem',
    },
    contactsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '1.5rem',
    },
    // AI Connector styles
    aiConnectorCard: {
      background: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '2px solid #e2e8f0',
      marginBottom: '2rem',
      overflow: 'hidden',
    },
    aiConnectorHeader: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'all 200ms',
      color: 'white',
    },
    aiConnectorTitle: {
      fontSize: '1.25rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    aiConnectorSubtitle: {
      fontSize: '0.85rem',
      opacity: 0.9,
      marginTop: '0.25rem',
    },
    aiConnectorContent: {
      maxHeight: '500px',
      overflowY: 'auto',
    },
    chatContainer: {
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      minHeight: '300px',
      maxHeight: '400px',
      overflowY: 'auto',
    },
    chatMessage: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      maxWidth: '80%',
    },
    userMessage: {
      alignSelf: 'flex-end',
      alignItems: 'flex-end',
    },
    assistantMessage: {
      alignSelf: 'flex-start',
      alignItems: 'flex-start',
    },
    messageBubble: {
      padding: '0.875rem 1.125rem',
      borderRadius: '14px',
      fontSize: '0.9rem',
      lineHeight: 1.5,
    },
    userBubble: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderBottomRightRadius: '4px',
    },
    assistantBubble: {
      background: '#f8fafc',
      color: '#0f172a',
      border: '1px solid #e2e8f0',
      borderBottomLeftRadius: '4px',
    },
    chatInputContainer: {
      padding: '1rem 1.5rem',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-end',
    },
    chatInput: {
      flex: 1,
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.9rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      resize: 'none',
      maxHeight: '100px',
      minHeight: '40px',
    },
    sendButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.75rem 1rem',
      border: 'none',
      borderRadius: '10px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      cursor: 'pointer',
      transition: 'all 200ms',
      fontWeight: 600,
    },
    connectionResultsContainer: {
      marginTop: '1rem',
      padding: '1rem',
      background: '#ffffff',
      borderRadius: '10px',
      border: '2px solid #e2e8f0',
    },
    resultsHeader: {
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    suggestionCard: {
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      padding: '1rem',
      marginBottom: '0.75rem',
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    suggestionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.5rem',
    },
    suggestionTitle: {
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    suggestionReason: {
      fontSize: '0.8rem',
      color: '#64748b',
      marginBottom: '0.5rem',
    },
    priorityBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.2rem 0.6rem',
      borderRadius: '6px',
      fontSize: '0.7rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    // Quick message styles
    quickMessageCard: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      color: 'white',
      marginBottom: '2rem',
    },
    quickMessageTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    quickMessageSubtitle: {
      fontSize: '0.95rem',
      opacity: 0.9,
      marginBottom: '1.5rem',
    },
    quickMessageForm: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1rem',
    },
    quickMessageSelect: {
      padding: '0.75rem 1rem',
      border: 'none',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: 'rgba(255, 255, 255, 0.95)',
      color: '#0f172a',
      outline: 'none',
      cursor: 'pointer',
      fontWeight: 500,
    },
    quickMessageButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'rgba(255, 255, 255, 0.95)',
      color: '#667eea',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    },
    generatedMessageBox: {
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '12px',
      padding: '1.5rem',
      marginTop: '1rem',
      color: '#0f172a',
    },
    generatedMessageText: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
    },
    messageActions: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
    },
    copyButton: {
      background: '#2563eb',
      color: 'white',
    },
    viewFullButton: {
      background: '#8b5cf6',
      color: 'white',
    },
    loadingDots: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
    },
    loadingDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.8)',
    },
    // Modal styles
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      padding: '2rem',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      paddingBottom: '1rem',
      borderBottom: '1px solid #e2e8f0',
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#64748b',
      padding: '0.5rem',
      borderRadius: '8px',
      transition: 'all 200ms',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '0.95rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      transition: 'all 200ms',
    },
    select: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      cursor: 'pointer',
    },
    textarea: {
      width: '95%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromPayment = urlParams.get('from_payment');
    
    // ✨ READ FROM LOCALSTORAGE (persists across navigation)
    const savedQuery = localStorage.getItem('linkedin_search_query_dashboard');
    const isPending = localStorage.getItem('linkedin_search_pending_dashboard');
    
    console.log('🔍 Dashboard post-payment check:', { fromPayment, savedQuery, isPending });
    
    if (fromPayment === 'linkedin_search' && savedQuery && isPending === 'true') {
      console.log('✅ Restoring dashboard search:', savedQuery);
      
      // Clear localStorage first
      localStorage.removeItem('linkedin_search_query_dashboard');
      localStorage.removeItem('linkedin_search_pending_dashboard');
      
      // Clear URL parameter
      window.history.replaceState({}, '', '/dashboard');
      
      // Reopen AI Connector with saved query
      setAiConnectorOpen(true);
      setUserInput(savedQuery);
      
      // Show success message
      showSuccess('Credits added! Searching for connections now...');
      
      // Auto-submit after delay to ensure modal is open
      setTimeout(() => {
        console.log('🚀 Auto-submitting dashboard search');
        handleSendChatMessage();
      }, 800);
    }
  }, []);


  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  useEffect(() => {
    if (aiConnectorOpen && chatMessages.length === 0) {
      // Initialize with welcome message
      setChatMessages([
        {
          role: 'assistant',
          content:
            "Hi! I'm your AI Connector. I can help you find valuable LinkedIn connections. Tell me what you're looking for:\n\n• By role or industry\n• By location\n• By company\n• By school or interests\n\nWhat kind of connections would help you grow your network?",
        },
      ]);
    }
  }, [aiConnectorOpen]);

  const loadDashboardData = async () => {
    try {
      const data = await contactService.getContacts();
      setContacts(data.contacts);
      
      // Calculate stats
      const now = new Date();
      const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
      
      const recentContacts = data.contacts.filter(c => {
        const lastContact = c.last_contacted ? new Date(c.last_contacted) : null;
        return lastContact && lastContact >= thirtyDaysAgo;
      });

      setStats({
        totalContacts: data.contacts.length,
        recentInteractions: recentContacts.length,
        activeConnections: data.contacts.filter(c => c.interaction_count > 2).length,
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const handleSendChatMessage = async () => {
    if (!userInput.trim() || isChatGenerating) return;

    const userMessage = userInput.trim();
    
    // Check feature access BEFORE processing
    try {
      const accessResponse = await fetch(`${API_URL}/api/subscription/check-access/linkedin_search`, {
        credentials: 'include',
      });
      
      const accessData = await accessResponse.json();
      
      if (!accessData.has_access) {
        // ✨ SAVE TO LOCALSTORAGE with unique dashboard key
        localStorage.setItem('linkedin_search_query_dashboard', userMessage);
        localStorage.setItem('linkedin_search_pending_dashboard', 'true');
        
        console.log('💳 No access - showing paywall, query saved:', userMessage);
        
        // Show paywall
        setPaywallFeatureInfo(accessData);
        setShowPaywall(true);
        return;
      }
      
      // Has access, proceed with search
      setUserInput('');
      setChatMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
      setIsChatGenerating(true);

      // Build conversation history for context
      const conversationHistory = chatMessages
        .map((msg) => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
        .join('\n');

      const response = await fetch(`${API_URL}/api/calendar/find-connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: userMessage,
          conversationHistory: conversationHistory,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Add assistant response
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.response || 'Here are the connections I found:',
            suggestions: data.suggestions || [],
          },
        ]);
        setConnectionSuggestions(data.suggestions || []);
      } else if (response.status === 403 && data.requires_purchase) {
        // Backend returned no credits - show paywall
        localStorage.setItem('linkedin_search_query_dashboard', userMessage);
        localStorage.setItem('linkedin_search_pending_dashboard', 'true');
        
        setPaywallFeatureInfo(data);
        setShowPaywall(true);
      } else {
        showError('Failed to find connections');
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try rephrasing your request.',
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      showError('Failed to process request');
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setIsChatGenerating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChatMessage();
    }
  };

  const handleSelectConnection = async (connection) => {
    setAddingConnection(true);
    try {
      // Add to contacts
      const response = await fetch(`${API_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: connection.name,
          company: connection.company,
          role: connection.current_role,
          linkedin_url: connection.linkedin_url,
          connection_origin: 'AI Recommended Connection',
          conversation_notes: connection.common_ground,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showSuccess(`${connection.name} added to contacts!`);
        await loadDashboardData(); // Refresh contacts

        // Set selected connection and open schedule modal
        setSelectedConnection({
          ...connection,
          contact_id: data.contact.id,
        });
        
        // Pre-fill event form
        setNewEvent({
          title: `Initial outreach to ${connection.name}`,
          contact_id: data.contact.id,
          event_type: 'coffee',
          start_date: moment().add(3, 'days').format('YYYY-MM-DD'),
          start_time: '14:00',
          duration: 30,
          notes: connection.reasoning,
          reminder: true,
        });

        setScheduleEventModalOpen(true);
      } else {
        showError('Failed to add connection');
      }
    } catch (error) {
      showError('Failed to add connection');
      console.error(error);
    } finally {
      setAddingConnection(false);
    }
  };

  const handleSaveEvent = async () => {
    if (!newEvent.title || !newEvent.contact_id || !newEvent.start_date) {
      showError('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/calendar/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newEvent),
      });

      if (response.ok) {
        showSuccess('Event scheduled successfully!');
        setScheduleEventModalOpen(false);
        setSelectedConnection(null);
        setNewEvent({
          title: '',
          contact_id: '',
          event_type: 'coffee',
          start_date: '',
          start_time: '',
          duration: 30,
          notes: '',
          reminder: true,
        });
      } else {
        showError('Failed to schedule event');
      }
    } catch (error) {
      console.error('Failed to save event:', error);
      showError('Failed to schedule event');
    }
  };

  const handleQuickGenerate = async () => {
    if (!quickMessageContact) {
      showWarning('Please select a contact first');
      return;
    }

    setIsGenerating(true);
    setGeneratedMessage('');

    try {
      const result = await aiService.generateMessage(
        parseInt(quickMessageContact),
        quickMessageType,
        quickMessageMedium,
        ''
      );
      
      setGeneratedMessage(result.message);
      showSuccess('Message generated!');
    } catch (error) {
      console.error('Failed to generate message:', error);
      showError('Failed to generate message');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(generatedMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showSuccess('Copied to clipboard!');
  };

  const handleViewFull = () => {
    navigate('/dashboard/messages', {
      state: {
        selectedContact: quickMessageContact,
        messageType: quickMessageType,
        medium: quickMessageMedium,
        generatedMessage: generatedMessage,
      }
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return { bg: '#fee2e2', text: '#dc2626' };
      case 'medium':
        return { bg: '#fef3c7', text: '#d97706' };
      case 'low':
        return { bg: '#dbeafe', text: '#2563eb' };
      default:
        return { bg: '#f1f5f9', text: '#64748b' };
    }
  };

  const recentContacts = contacts
    .sort((a, b) => {
      const dateA = a.last_contacted ? new Date(a.last_contacted) : new Date(0);
      const dateB = b.last_contacted ? new Date(b.last_contacted) : new Date(0);
      return dateB - dateA;
    })
    .slice(0, 6);

  const statCards = [
    {
      label: 'Total Contacts',
      value: stats.totalContacts,
      icon: Users,
      color: '#667eea',
      bg: 'rgba(102, 126, 234, 0.1)',
    },
    {
      label: 'Recent Activity',
      value: stats.recentInteractions,
      icon: MessageSquare,
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.1)',
    },
    {
      label: 'Active Connections',
      value: stats.activeConnections,
      icon: TrendingUp,
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.1)',
    },
  ];

  const LoadingDots = () => (
    <div style={styles.loadingDots}>
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
      />
    </div>
  );

  return (
    <div style={styles.page}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <PaywallModal
        isOpen={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          // ✨ If they close without buying, restore from localStorage
          const savedQuery = localStorage.getItem('linkedin_search_query_dashboard');
          if (savedQuery) {
            setUserInput(savedQuery);
            // Keep it in localStorage in case they try again
          }
        }}
        featureInfo={paywallFeatureInfo}
        sourcePage="dashboard"  // ✨ NEW: Identify source
        onPurchaseComplete={() => {
          setShowPaywall(false);
          // Search will happen automatically in useEffect after redirect
        }}
      />

      <div style={styles.header}>
        <h1 style={styles.title}>Dashboard</h1>
        <p style={styles.subtitle}>
          Your networking overview and recent activity
        </p>
      </div>

      <div style={styles.statsGrid}>
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -4, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }}
              style={styles.statCard}
            >
              <div style={{
                ...styles.iconBox,
                background: stat.bg,
                color: stat.color,
              }}>
                <Icon size={24} />
              </div>
              <div style={styles.statInfo}>
                <div style={styles.statLabel}>{stat.label}</div>
                <div style={styles.statValue}>{stat.value}</div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* AI Connector Chat Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={styles.aiConnectorCard}
      >
        <div
          style={styles.aiConnectorHeader}
          onClick={() => setAiConnectorOpen(!aiConnectorOpen)}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.95';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <div>
            <div style={styles.aiConnectorTitle}>
              <Sparkles size={24} />
              Your AI Connector
            </div>
            <div style={styles.aiConnectorSubtitle}>
              Find and connect with valuable LinkedIn contacts
            </div>
          </div>
          {aiConnectorOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </div>

        <AnimatePresence>
          {aiConnectorOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={styles.aiConnectorContent}
            >
              <div style={styles.chatContainer}>
                {chatMessages.map((message, idx) => (
                  <div
                    key={idx}
                    style={{
                      ...styles.chatMessage,
                      ...(message.role === 'user' ? styles.userMessage : styles.assistantMessage),
                    }}
                  >
                    <div
                      style={{
                        ...styles.messageBubble,
                        ...(message.role === 'user' ? styles.userBubble : styles.assistantBubble),
                      }}
                    >
                      {message.content}
                    </div>

                    {message.suggestions && message.suggestions.length > 0 && (
                      <div style={styles.connectionResultsContainer}>
                        <div style={styles.resultsHeader}>
                          <Users size={18} />
                          Found {message.suggestions.length} connection
                          {message.suggestions.length !== 1 ? 's' : ''}
                        </div>
                        {message.suggestions.map((suggestion, sIdx) => {
                          const priorityColor = getPriorityColor(suggestion.priority);
                          return (
                            <div
                              key={sIdx}
                              style={styles.suggestionCard}
                              onClick={() => handleSelectConnection(suggestion)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#cbd5e1';
                                e.currentTarget.style.background = '#ffffff';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.background = '#f8fafc';
                              }}
                            >
                              <div style={styles.suggestionHeader}>
                                <div style={{ flex: 1 }}>
                                  <div style={styles.suggestionTitle}>{suggestion.name}</div>
                                  <div style={styles.suggestionReason}>{suggestion.reasoning}</div>
                                </div>
                                <span
                                  style={{
                                    ...styles.priorityBadge,
                                    background: priorityColor.bg,
                                    color: priorityColor.text,
                                  }}
                                >
                                  {suggestion.priority}
                                </span>
                              </div>
                              <div
                                style={{
                                  marginTop: '0.5rem',
                                  fontSize: '0.8rem',
                                  color: '#64748b',
                                }}
                              >
                                <div>
                                  <strong>Role:</strong> {suggestion.current_role} at{' '}
                                  {suggestion.company}
                                </div>
                                <div style={{ marginTop: '0.25rem' }}>
                                  <strong>Location:</strong> {suggestion.location}
                                </div>
                                {suggestion.common_ground && (
                                  <div style={{ marginTop: '0.25rem' }}>
                                    <strong>Common Ground:</strong> {suggestion.common_ground}
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  marginTop: '0.75rem',
                                  padding: '0.5rem',
                                  background: '#eff6ff',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  color: '#1e40af',
                                  textAlign: 'center',
                                }}
                              >
                                Click to add and schedule outreach
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {isChatGenerating && (
                  <div style={{ ...styles.chatMessage, ...styles.assistantMessage }}>
                    <div style={{ ...styles.messageBubble, ...styles.assistantBubble }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#667eea',
                        }}
                      >
                        <Loader size={16} className="animate-spin" />
                        Searching LinkedIn...
                      </div>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              <div style={styles.chatInputContainer}>
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g., 'Find VCs in Boston' or 'Software engineers from MIT'"
                  style={styles.chatInput}
                  disabled={isChatGenerating}
                  rows={1}
                />
                <button
                  onClick={handleSendChatMessage}
                  disabled={!userInput.trim() || isChatGenerating}
                  style={{
                    ...styles.sendButton,
                    opacity: !userInput.trim() || isChatGenerating ? 0.5 : 1,
                    cursor: !userInput.trim() || isChatGenerating ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Send size={18} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Quick Message Generation Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        style={styles.quickMessageCard}
      >
        <div style={styles.quickMessageTitle}>
          <Sparkles size={24} />
          Quick Message Generator
        </div>
        <div style={styles.quickMessageSubtitle}>
          Generate a networking message in seconds
        </div>
        
        <div style={styles.quickMessageForm}>
          <select
            value={quickMessageContact}
            onChange={(e) => setQuickMessageContact(e.target.value)}
            style={styles.quickMessageSelect}
          >
            <option value="">Select Contact...</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.name}
              </option>
            ))}
          </select>

          <select
            value={quickMessageType}
            onChange={(e) => setQuickMessageType(e.target.value)}
            style={styles.quickMessageSelect}
          >
            <option value="follow_up">Follow-up</option>
            <option value="introduction">Introduction</option>
            <option value="response">Response</option>
            <option value="check_in">Check-in</option>
            <option value="thank_you">Thank You</option>
          </select>

          <select
            value={quickMessageMedium}
            onChange={(e) => setQuickMessageMedium(e.target.value)}
            style={styles.quickMessageSelect}
          >
            <option value="email">Email</option>
            <option value="text">Text Message</option>
            <option value="linkedin_message">LinkedIn Message</option>
          </select>

          <button
            onClick={handleQuickGenerate}
            disabled={isGenerating || !quickMessageContact}
            style={{
              ...styles.quickMessageButton,
              opacity: isGenerating || !quickMessageContact ? 0.6 : 1,
              cursor: isGenerating || !quickMessageContact ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!isGenerating && quickMessageContact) {
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isGenerating && quickMessageContact) {
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <Send size={18} />
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>

        {isGenerating && <LoadingDots />}

        {generatedMessage && !isGenerating && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={styles.generatedMessageBox}
          >
            <div style={styles.generatedMessageText}>{generatedMessage}</div>
            <div style={styles.messageActions}>
              <button
                onClick={handleCopyMessage}
                style={{
                  ...styles.actionButton,
                  ...styles.copyButton,
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleViewFull}
                style={{
                  ...styles.actionButton,
                  ...styles.viewFullButton,
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <Sparkles size={16} />
                View Full Generator
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
      
      <UpcomingDates 
        showSuccess={showSuccess}
        showError={showError}
      />

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Recent Contacts</h2>
        {recentContacts.length > 0 ? (
          <div style={styles.contactsGrid}>
            {recentContacts.map((contact, index) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ContactCard
                  contact={contact}
                  onClick={() => navigate(`/dashboard/contacts/${contact.id}`)}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#64748b',
            background: '#ffffff',
            borderRadius: '16px',
            border: '2px dashed #e2e8f0',
          }}>
            <Calendar size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p style={{ fontSize: '1.1rem' }}>No recent contacts yet</p>
            <p style={{ fontSize: '0.95rem', marginTop: '0.5rem' }}>
              Start adding contacts to see them here
            </p>
          </div>
        )}
      </div>

      {/* Schedule Event Modal */}
      <AnimatePresence>
        {scheduleEventModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={() => setScheduleEventModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Schedule Outreach</h3>
                <button
                  onClick={() => setScheduleEventModalOpen(false)}
                  style={styles.closeButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Event Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  placeholder="e.g., Coffee chat with Sarah"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Event Type</label>
                <select
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                  style={styles.select}
                >
                  <option value="call">Phone Call</option>
                  <option value="meeting">Meeting</option>
                  <option value="coffee">Coffee Chat</option>
                  <option value="followup">Follow-up</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  gap: '1rem',
                }}
              >
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date *</label>
                  <input
                    type="date"
                    value={newEvent.start_date}
                    onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Time</label>
                  <input
                    type="time"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Duration (min)</label>
                  <input
                    type="number"
                    value={newEvent.duration}
                    onChange={(e) =>
                      setNewEvent({
                        ...newEvent,
                        duration: parseInt(e.target.value),
                      })
                    }
                    min="15"
                    step="15"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes / Agenda</label>
                <textarea
                  value={newEvent.notes}
                  onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                  placeholder="What do you want to discuss?"
                  style={styles.textarea}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  onClick={() => setScheduleEventModalOpen(false)}
                  style={{
                    ...styles.actionButton,
                    background: '#e2e8f0',
                    color: '#64748b',
                    flex: 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEvent}
                  style={{
                    ...styles.actionButton,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    flex: 1,
                  }}
                >
                  <Save size={18} />
                  Schedule Event
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;